import { tw } from '../../../../universal';
import { _, React, xlsx } from '../../../lib';
import { ButtonTW } from '../buttons';
import { ListGrid } from '../list-grid';
import { LoadingSpinnerLarge } from '../loading';
import { TabControl } from '../tabs';
const DEFAULT_MAX_ROWS = 2000;
export const XlsxPreview = (props) => {
    const { url, maxRows = DEFAULT_MAX_ROWS, filename } = props;
    const [sheets, setSheets] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [selectedSheet, setSelectedSheet] = React.useState('');
    const [selectedRow, setSelectedRow] = React.useState(null);
    React.useEffect(() => {
        const fetchAndParse = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Failed to fetch file: ${response.statusText}`);
                }
                const arrayBuffer = await response.arrayBuffer();
                const workbook = xlsx.read(arrayBuffer, { type: 'array' });
                const parsedSheets = _.chain(workbook.SheetNames)
                    .map(sheetName => {
                    const sheet = workbook.Sheets[sheetName];
                    if (!sheet) {
                        return null;
                    }
                    const rawData = xlsx.utils.sheet_to_json(sheet, {
                        header: 1,
                    });
                    if (rawData.length === 0) {
                        return {
                            name: sheetName,
                            headers: [],
                            rows: [],
                            totalRows: 0,
                        };
                    }
                    const headers = _.map(rawData[0], (cell, idx) => cell != null ? String(cell) : `Column ${idx + 1}`);
                    const dataRows = rawData.slice(1);
                    const totalRows = dataRows.length;
                    const limitedRows = dataRows.slice(0, maxRows);
                    const rows = limitedRows.map((row, rowIdx) => {
                        const record = { _rowIndex: rowIdx };
                        headers.forEach((_header, colIdx) => {
                            const cellValue = row[colIdx];
                            record[`col_${colIdx}`] = cellValue ?? '';
                        });
                        return record;
                    });
                    return {
                        name: sheetName,
                        headers,
                        rows,
                        totalRows,
                    };
                })
                    .compact()
                    .value();
                setSheets(parsedSheets);
                const firstSheet = parsedSheets[0];
                if (firstSheet) {
                    setSelectedSheet(firstSheet.name);
                }
            }
            catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to parse spreadsheet';
                setError(message);
            }
            finally {
                setLoading(false);
            }
        };
        void fetchAndParse();
    }, [url, maxRows]);
    if (loading) {
        return React.createElement(LoadingSpinnerLarge, null);
    }
    if (error) {
        return (React.createElement("div", { className: tw `w-full h-full flex items-center justify-center` },
            React.createElement("div", { className: tw `text-red-600 text-center p-4` },
                React.createElement("div", { className: tw `font-semibold mb-2` }, "Error loading spreadsheet"),
                React.createElement("div", { className: tw `text-sm` }, error))));
    }
    const currentSheet = _.find(sheets, s => s.name === selectedSheet);
    const fields = currentSheet
        ? _.map(currentSheet.headers, (header, colIdx) => ({
            key: `col_${colIdx}`,
            lbl: header,
            text: (record) => {
                const val = record[`col_${colIdx}`];
                return val != null ? String(val) : '';
            },
        }))
        : [];
    const renderContent = () => {
        if (!currentSheet) {
            return (React.createElement("div", { className: tw `w-full h-full flex items-center justify-center text-gray-500` }, "No data available"));
        }
        const isTruncated = currentSheet.totalRows > maxRows;
        return (React.createElement("div", { className: tw `w-full h-full flex flex-col` },
            isTruncated && (React.createElement("div", { className: tw `bg-amber-100 text-amber-800 px-3 py-2 text-sm border-b border-amber-200` },
                "Showing first ",
                maxRows.toLocaleString(),
                " of",
                ' ',
                currentSheet.totalRows.toLocaleString(),
                " rows")),
            React.createElement("div", { className: tw `flex-1 min-h-0` },
                React.createElement(ListGrid, { data: currentSheet.rows, fields: fields, pk: (record) => record._rowIndex, value: selectedRow, onUpdate: setSelectedRow, multiple: false, readOnly: true, defaultSortingKey: fields[0]?.key ?? 'col_0' }))));
    };
    const handleDownload = () => {
        window.open(url, '_blank');
    };
    const toolbar = (React.createElement("div", { className: tw `flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50` },
        React.createElement("div", { className: tw `flex items-center gap-3` },
            filename && (React.createElement("span", { className: tw `text-sm font-medium text-gray-700` }, filename)),
            React.createElement("span", { className: tw `text-sm text-gray-500 italic` }, "Read-only")),
        React.createElement(ButtonTW, { type: "borderless", img: "/static/img/i8/color-download-from-the-cloud.svg", lbl: "Download", title: "Download the full spreadsheet file", onClick: handleDownload })));
    if (sheets.length <= 1) {
        return (React.createElement("div", { className: tw `w-full h-full flex flex-col` },
            toolbar,
            React.createElement("div", { className: tw `flex-1 min-h-0` }, renderContent())));
    }
    const tabs = _.map(sheets, sheet => ({
        value: sheet.name,
        label: sheet.name,
        content: renderContent,
    }));
    return (React.createElement("div", { className: tw `w-full h-full flex flex-col` },
        toolbar,
        React.createElement("div", { className: tw `flex-1 min-h-0` },
            React.createElement(TabControl, { tabs: tabs, value: selectedSheet, onUpdate: setSelectedSheet }))));
};
//# sourceMappingURL=xlsx-preview.js.map